if (!customElements.get('marame-bag-motion')) {
  class MarameBagMotion extends HTMLElement {
    static totals = new WeakMap();
    static details = new WeakMap();

    connectedCallback() {
      this.disconnectedCallback();
      this.drawer = this.closest('cart-drawer');
      if (!this.drawer) return;
      this.events = new AbortController();
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.media.addEventListener?.('change', () => {
        if (this.media.matches) { this.clearTotalMotion(); this.clearDetailsMotion(); }
        this.schedule();
      }, { signal: this.events.signal });
      window.addEventListener('resize', () => this.schedule(), { signal: this.events.signal });
      this.addEventListener('scroll', () => this.schedule(), { capture: true, passive: true, signal: this.events.signal });
      this.contentObserver = new MutationObserver(() => this.schedule());
      this.contentObserver.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
      this.drawerObserver = new MutationObserver(() => this.schedule());
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class', 'inert'] });
      this.resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.schedule());
      this.resizeTargets = new Set();
      this.detailsReady = false;
      this.refresh();
      this.schedule();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.contentObserver?.disconnect();
      this.drawerObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.clearTotalMotion();
      this.clearDetailsMotion();
    }

    schedule() {
      if (this.frame || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.isConnected) { this.detailsReady = true; this.refresh(); }
      });
    }

    refresh() {
      const scroll = this.querySelector('[data-bag-scroll]');
      const cue = this.querySelector('[data-bag-scroll-cue]');
      const targets = new Set([this, scroll, ...(scroll?.children || [])].filter(Boolean));
      this.resizeTargets?.forEach(target => {
        if (!targets.has(target)) this.resizeObserver?.unobserve(target);
      });
      targets.forEach(target => {
        if (!this.resizeTargets?.has(target)) this.resizeObserver?.observe(target);
      });
      this.resizeTargets = targets;
      const visible = !this.hidden && !this.inert && !this.drawer.inert && this.drawer.classList.contains('active') && !this.drawer.classList.contains('is-empty');
      if (!visible) {
        if (this.totalAnimations?.length) this.clearTotalMotion();
        this.clearDetailsMotion();
      }
      if (this.detailsReady) this.refreshDetails(visible);
      if (cue) cue.dataset.more = String(Boolean(visible && scroll?.clientHeight > 0 && scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight > 32));

      const total = this.querySelector('[data-bag-total]');
      const value = total?.querySelector('[data-bag-total-value]');
      if (!value) return;
      const next = { key: total.dataset.bagTotal, text: value.textContent.trim() };
      const previous = MarameBagMotion.totals.get(this.drawer);
      MarameBagMotion.totals.set(this.drawer, next);
      if (!previous || (previous.key === next.key && previous.text === next.text)) return;
      this.clearTotalMotion();
      if (!visible || this.media.matches || typeof value.animate !== 'function') return;

      // Keep the latest server total accessible throughout the decorative transition.
      this.ghost = document.createElement('span');
      this.ghost.className = 'marame-bag__previous-total';
      this.ghost.setAttribute('aria-hidden', 'true');
      this.ghost.textContent = previous.text;
      total.append(this.ghost);
      this.totalAnimations = [
        this.ghost.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-4px)' }], { duration: 220, easing: 'ease-in-out', fill: 'forwards' }),
        value.animate([{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 220, delay: 220, easing: 'ease-in-out', fill: 'both' })
      ];
      const run = this.motionRun;
      this.totalAnimations[1].onfinish = () => { if (run === this.motionRun) this.clearTotalMotion(); };
      this.motionTimer = setTimeout(() => { if (run === this.motionRun) this.clearTotalMotion(); }, 560);
    }

    refreshDetails(visible) {
      const previous = MarameBagMotion.details.get(this.drawer);
      const reminder = this.querySelector('marame-bag-fit-note');
      const content = reminder?.querySelector('[data-bag-fit-content]');
      const fitKey = reminder && !reminder.hidden && content?.childElementCount ? content.textContent.trim() : '';
      const fit = fitKey ? {
        key: fitKey, height: reminder.getBoundingClientRect().height,
        content: previous?.fit?.key === fitKey ? previous.fit.content : content.cloneNode(true)
      } : null;
      const shipping = this.querySelector('[data-marame-shipping]');
      const progress = shipping?.querySelector('progress');
      const shippingState = progress ? {
        context: `${shipping.querySelector('.marame-shipping__destination')?.textContent.trim()}|${progress.max}`,
        value: Math.max(0, Math.min(1, progress.value / progress.max)),
        ink: getComputedStyle(shipping).getPropertyValue('--shipping-ink').trim(),
        track: getComputedStyle(shipping).getPropertyValue('--shipping-track').trim()
      } : null;
      const entering = visible && !previous?.visible;
      const fitChanged = (previous && previous.fit?.key !== fit?.key) || (entering && fit);
      const shippingChanged = previous?.shipping && shippingState && previous.shipping.context === shippingState.context && previous.shipping.value !== shippingState.value;
      MarameBagMotion.details.set(this.drawer, { fit, shipping: shippingState, visible });
      if (!visible || this.media.matches || typeof this.animate !== 'function' || (!fitChanged && !shippingChanged)) return;
      this.clearDetailsMotion();
      this.detailAnimations = [];
      this.detailCleanups = [];
      if (fitChanged) this.animateFit(entering ? null : previous?.fit, fit, reminder);
      if (shippingChanged) this.animateShipping(previous.shipping, shippingState, progress);
      const run = this.detailsRun;
      this.detailsTimer = setTimeout(() => { if (run === this.detailsRun) this.clearDetailsMotion(); }, 780);
    }

    animateFit(previous, next, reminder) {
      const anchor = reminder || this.querySelector('.marame-bag__scroll-frame');
      if (!anchor?.parentNode) return;
      const frame = document.createElement('div');
      frame.className = 'marame-bag__fit-motion';
      anchor.before(frame);
      let visual;
      if (next) {
        visual = reminder;
        frame.append(visual);
        this.detailCleanups.push(() => { if (frame.isConnected) frame.replaceWith(visual); });
      } else {
        visual = document.createElement('div');
        visual.className = 'marame-bag__fit-ghost';
        visual.setAttribute('aria-hidden', 'true');
        visual.inert = true;
        visual.append(previous.content.cloneNode(true));
        frame.append(visual);
        this.detailCleanups.push(() => frame.remove());
      }
      const options = { duration: 380, easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'both' };
      const height = frame.animate([{ height: `${previous?.height || 0}px` }, { height: `${next?.height || 0}px` }], options);
      const opacity = visual.animate([{ opacity: previous ? 1 : 0 }, { opacity: next ? 1 : 0 }], options);
      this.detailAnimations.push(height, opacity);
      const run = this.detailsRun;
      height.onfinish = () => {
        if (run !== this.detailsRun) return;
        const cleanup = this.detailCleanups.shift();
        height.onfinish = null;
        height.cancel(); opacity.cancel();
        cleanup?.();
      };
    }

    animateShipping(previous, next, progress) {
      const frame = document.createElement('div');
      frame.className = 'marame-bag__progress-motion';
      progress.before(frame);
      frame.append(progress);
      const track = document.createElement('span');
      const bar = document.createElement('span');
      track.className = 'marame-bag__progress-track';
      bar.className = 'marame-bag__progress-value';
      track.setAttribute('aria-hidden', 'true');
      track.append(bar); frame.append(track);
      // The native progress element retains the final, server-confirmed accessible value.
      const opacity = progress.style.opacity;
      progress.style.opacity = '0';
      const options = { duration: 700, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'both' };
      const width = bar.animate([
        { transform: `scaleX(${previous.value})`, backgroundColor: previous.ink },
        { transform: `scaleX(${next.value})`, backgroundColor: next.ink }
      ], options);
      const colour = track.animate([{ backgroundColor: previous.track }, { backgroundColor: next.track }], options);
      this.detailAnimations.push(width, colour);
      const cleanup = () => { progress.style.opacity = opacity; if (frame.isConnected) frame.replaceWith(progress); };
      this.detailCleanups.push(cleanup);
      const run = this.detailsRun;
      width.onfinish = () => {
        if (run !== this.detailsRun) return;
        width.onfinish = null; width.cancel(); colour.cancel();
        this.detailCleanups = this.detailCleanups.filter(callback => callback !== cleanup);
        cleanup();
      };
    }

    clearDetailsMotion() {
      this.detailsRun = (this.detailsRun || 0) + 1;
      clearTimeout(this.detailsTimer);
      this.detailsTimer = null;
      this.detailAnimations?.forEach(animation => { animation.onfinish = null; animation.cancel(); });
      this.detailAnimations = [];
      this.detailCleanups?.forEach(cleanup => cleanup());
      this.detailCleanups = [];
    }

    clearTotalMotion() {
      this.motionRun = (this.motionRun || 0) + 1;
      clearTimeout(this.motionTimer);
      this.motionTimer = null;
      this.totalAnimations?.forEach(animation => { animation.onfinish = null; animation.cancel(); });
      this.totalAnimations = [];
      this.ghost?.remove();
      this.ghost = null;
    }
  }
  customElements.define('marame-bag-motion', MarameBagMotion);
}
